import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Globe, RefreshCw, Loader2, CheckCircle2, UserPlus, FileText, List, CheckSquare, Search, X, ChevronUp, ChevronDown, ChevronRight, ChevronsUpDown, AlertTriangle } from 'lucide-react'
import {
  getUnmatchedExportFees,
  getMatchedExportFees,
  getExportInvoiceSummary,
  getExportInvoiceDrillIn,
  syncExportFees,
  syncOrders,
  getAdminClients,
  assignExportFee,
} from '../../lib/api'
import { useNavigate } from 'react-router-dom'
import { ToastContainer, useToast } from '../../components/Toast'

function formatCurrency(v, currency = 'AUD') {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(v ?? 0)
}

// Mirror of ExportsTab.classifySource — keeps the source badge consistent
// across the client Exports tab and this admin page. FedEx duty PDF rows
// land in export_fees with courier='fedex' and zonos_order_id=NULL, so the
// presence/absence of zonos_order_id is the discriminator.
function classifySource(fee) {
  if (fee.manual) return { key: 'manual', label: 'Manual', cls: 'bg-violet-100 text-violet-700' }
  if ((fee.courier || '').toLowerCase() === 'fedex' && !fee.zonos_order_id) {
    return { key: 'fedex', label: 'FedEx', cls: 'bg-orange-100 text-orange-700' }
  }
  return { key: 'zonos', label: 'Zonos', cls: 'bg-blue-100 text-blue-700' }
}

// ── Sortable column header ────────────────────────────────────────────────────

function SortableTH({ col, label, sort, onSort, align = 'left' }) {
  const isActive = sort.col === col
  const Icon = !isActive
    ? <ChevronsUpDown size={11} className="text-slate-300 ml-1 inline-block" />
    : sort.dir === 'asc'
      ? <ChevronUp size={11} className="text-blue-500 ml-1 inline-block" />
      : <ChevronDown size={11} className="text-blue-500 ml-1 inline-block" />
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-3 font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap select-none cursor-pointer hover:text-slate-700 hover:bg-slate-100 transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {label}
      {Icon}
    </th>
  )
}

// ── Inline client assign cell ─────────────────────────────────────────────────

function AssignCell({ feeId, clients, onAssigned }) {
  const [open, setOpen]       = useState(false)
  const [selected, setSelected] = useState('')
  const [orderNum, setOrderNum] = useState('')
  const { toasts, addToast } = useToast()

  const mutation = useMutation({
    mutationFn: () => assignExportFee(feeId, selected, orderNum.trim() || undefined),
    onSuccess: () => {
      setOpen(false)
      setOrderNum('')
      onAssigned()
    },
    onError: (err) => {
      addToast({ type: 'error', message: `Assign failed: ${err.response?.data?.detail || err.message}` })
    },
  })

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-blue-600 transition-colors group"
      >
        <UserPlus size={13} className="group-hover:scale-110 transition-transform" />
        Assign
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1.5">
      <select
        autoFocus
        value={selected}
        onChange={e => setSelected(e.target.value)}
        className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 max-w-[160px]"
      >
        <option value="">Select client…</option>
        {clients.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      <input
        type="text"
        value={orderNum}
        onChange={e => setOrderNum(e.target.value)}
        placeholder="Order # (optional)"
        title="Optional — links the fee to an existing portal order so it shows under that order in reports."
        className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 w-32"
        onKeyDown={(e) => { if (e.key === 'Enter' && selected) mutation.mutate() }}
      />
      <button
        onClick={() => mutation.mutate()}
        disabled={!selected || mutation.isPending}
        className="btn-primary text-xs py-1 px-2 disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 size={11} className="animate-spin" /> : 'Save'}
      </button>
      <button
        onClick={() => { setOpen(false); setSelected(''); setOrderNum('') }}
        className="text-xs text-slate-400 hover:text-slate-600 px-1"
      >
        ✕
      </button>
    </div>
  )
}

// ── Invoice Summary tab ───────────────────────────────────────────────────────

function formatDate(str) {
  if (!str) return '—'
  try {
    const [y, m, d] = str.split('-')
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`
  } catch { return str }
}

function InvoiceSummaryRow({ inv, expanded, onToggle, onClientClick }) {
  const { data: drill, isLoading: drillLoading } = useQuery({
    queryKey: ['exports', 'invoices', inv.period_end, 'drill'],
    queryFn:  () => getExportInvoiceDrillIn(inv.period_end),
    enabled: expanded,
  })

  return (
    <>
      <tr
        onClick={onToggle}
        className={`cursor-pointer transition-colors ${expanded ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
      >
        <td className="pl-4 pr-2 py-4 text-slate-400">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-6 py-4 text-slate-700 whitespace-nowrap">{formatDate(inv.period_end)}</td>
        <td className="px-6 py-4">
          <span className="text-blue-600 font-mono text-sm">{inv.invoice_number}</span>
          <span className="ml-2 text-[10px] text-slate-400 uppercase tracking-wider">{inv.record_count} fee{inv.record_count !== 1 ? 's' : ''}</span>
        </td>
        <td className="px-6 py-4 text-slate-700 whitespace-nowrap">{formatDate(inv.due_date)}</td>
        <td className="px-6 py-4 text-right tabular-nums font-medium text-slate-800">{formatCurrency(inv.total)} AUD</td>
      </tr>

      {expanded && (
        <tr className="bg-slate-50/60">
          <td colSpan={5} className="px-6 py-4">
            {drillLoading || !drill ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Loader2 size={12} className="animate-spin" /> Loading invoice details…
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary chips */}
                <div className="flex flex-wrap gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {drill.matched_count} matched
                  </span>
                  {drill.unmatched_count > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      {drill.unmatched_count} unmatched
                    </span>
                  )}
                  {drill.manual_count > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      {drill.manual_count} manual
                    </span>
                  )}
                </div>

                {/* Per-client roll-up */}
                {drill.by_client.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Per-Client Total</p>
                    <div className="flex flex-wrap gap-2">
                      {drill.by_client.map((c) => (
                        <button
                          key={c.client_id}
                          onClick={(e) => { e.stopPropagation(); onClientClick(c.client_id) }}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 text-xs text-slate-700"
                        >
                          <span className="font-medium">{c.client_name}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500">{c.fee_count}&nbsp;fee{c.fee_count !== 1 ? 's' : ''}</span>
                          <span className="text-slate-400">·</span>
                          <span className="font-semibold tabular-nums">{formatCurrency(c.total)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-fee breakdown */}
                <div className="card overflow-hidden border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100/60">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">Tracking</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">Fee Date</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Duty</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Tax</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Fees</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Shipping</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wider">Charged To</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-100">
                        {drill.fees.map((f) => (
                          <tr key={f.id} className={`${!f.client_id ? 'bg-amber-50/40' : ''}`}>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                              {f.client_id ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); onClientClick(f.client_id) }}
                                  className="text-blue-600 hover:underline"
                                >
                                  {f.client_name || '(no name)'}
                                </button>
                              ) : (
                                <span className="text-amber-700">unmatched</span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-slate-700">{f.order_number || <span className="text-slate-300">—</span>}</td>
                            <td className="px-3 py-2 font-mono text-slate-500">{f.tracking_number || '—'}</td>
                            <td className="px-3 py-2 text-slate-500">{f.fee_date || '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(f.duty_amount, f.currency)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(f.tax_amount, f.currency)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(f.fee_amount, f.currency)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(f.shipping_amount, f.currency)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{formatCurrency(f.total_amount, f.currency)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {f.billing_cycle ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{f.billing_cycle}</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Not yet billed</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50">
                        <tr>
                          <td colSpan={8} className="px-3 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                            Invoice total ({drill.record_count} fee{drill.record_count !== 1 ? 's' : ''})
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-800">{formatCurrency(drill.total)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}


function InvoiceSummaryTab() {
  const [expanded, setExpanded] = useState(null)   // billing_date currently expanded
  const navigate = useNavigate()

  const { data: invoices = [], isLoading, error } = useQuery({
    queryKey: ['exports', 'invoices'],
    queryFn: getExportInvoiceSummary,
    refetchInterval: 120_000,
  })

  const grandTotal = invoices.reduce((s, r) => s + (r.total ?? 0), 0)

  return (
    <div className="card overflow-hidden">
      {isLoading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : error ? (
        /* `invoices = []` would otherwise render the "no invoice data" state
           below, so a failed fetch reads as "nothing invoiced". */
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <AlertTriangle size={32} className="text-red-400" />
          <p className="text-sm font-medium text-red-600">Failed to load invoice summary</p>
          <p className="text-xs text-red-500 max-w-md text-center">
            This is a load failure, <strong>not</strong> an empty list — export invoices may exist
            and none of their totals are shown here.
          </p>
          <p className="text-xs text-slate-400 font-mono">
            {error?.response?.data?.detail || error?.message || 'Unknown error'}
          </p>
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <FileText size={32} className="text-slate-300" />
          <p className="text-sm font-medium text-slate-600">No invoice data found</p>
          <p className="text-xs text-slate-400">Sync exports to pull records from Zonos</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200">
              <tr className="text-left">
                <th className="px-6 py-3 w-8" />
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Period end</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Invoice Number</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Due</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Invoice total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((inv) => (
                <InvoiceSummaryRow
                  key={inv.period_end}
                  inv={inv}
                  expanded={expanded === inv.period_end}
                  onToggle={() => setExpanded(expanded === inv.period_end ? null : inv.period_end)}
                  onClientClick={(cid) => navigate(`/admin/clients/${cid}`)}
                />
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50">
              <tr>
                <td colSpan={4} className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Total ({invoices.length} invoice{invoices.length !== 1 ? 's' : ''})
                </td>
                <td className="px-6 py-3 text-right tabular-nums font-bold text-slate-800">
                  {formatCurrency(grandTotal)} AUD
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Matched Exports tab ───────────────────────────────────────────────────────

// Default sort: newest synced first
const MATCHED_DEFAULT_SORT = { col: 'synced_at', dir: 'desc' }

function MatchedExportsTab() {
  const [search, setSearch] = useState('')
  const [page, setPage]     = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [sort, setSort]     = useState(MATCHED_DEFAULT_SORT)

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['exports', 'matched'],
    queryFn:  () => getMatchedExportFees(),
    refetchInterval: 120_000,
  })

  const filtered = rows.filter(r => {
    if (!search.trim()) return true
    const q = search.trim().toLowerCase()
    return (
      (r.client_name      || '').toLowerCase().includes(q) ||
      (r.order_number     || '').toLowerCase().includes(q) ||
      (r.tracking_number  || '').toLowerCase().includes(q) ||
      (r.courier          || '').toLowerCase().includes(q) ||
      (r.notes            || '').toLowerCase().includes(q) ||
      (r.billing_cycle    || '').toLowerCase().includes(q)
    )
  })

  // Sort the filtered set per current sort state. Number columns compare
  // numerically; everything else falls back to localeCompare. We always use
  // synced_at as a tiebreaker so rows that share the same primary sort key
  // (e.g. dozens of fees with the same fee_date) still come out in a stable,
  // chronological order rather than whatever the JS engine happens to return.
  const NUMERIC_COLS = new Set(['duty_amount', 'tax_amount', 'fee_amount', 'shipping_amount', 'total_amount'])
  function compareCol(a, b, k, dir) {
    let av = a[k], bv = b[k]
    if (NUMERIC_COLS.has(k)) {
      av = Number(av || 0); bv = Number(bv || 0)
      return dir === 'asc' ? av - bv : bv - av
    }
    av = (av ?? '').toString().toLowerCase()
    bv = (bv ?? '').toString().toLowerCase()
    if (av === bv) return 0
    return dir === 'asc' ? (av < bv ? -1 : 1) : (av < bv ? 1 : -1)
  }
  const sorted = [...filtered].sort((a, b) => {
    const primary = compareCol(a, b, sort.col, sort.dir)
    if (primary !== 0) return primary
    if (sort.col !== 'synced_at') {
      // Tiebreaker: most-recent sync first, regardless of primary sort direction
      return compareCol(a, b, 'synced_at', 'desc')
    }
    return 0
  })

  function handleSort(col) {
    setSort(s => s.col === col
      ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: NUMERIC_COLS.has(col) || col.endsWith('_at') || col.endsWith('_date') ? 'desc' : 'asc' }
    )
    setPage(1)
  }

  // Reset to page 1 whenever search/pageSize changes
  if (page > 1 && (page - 1) * pageSize >= sorted.length) {
    setTimeout(() => setPage(1), 0)
  }

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const safePage   = Math.min(page, totalPages)
  const pageRows   = sorted.slice((safePage - 1) * pageSize, safePage * pageSize)
  const totalAmount = sorted.reduce((s, r) => s + (r.total_amount || 0), 0)

  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="label">Search</label>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Client, order #, tracking, billing cycle…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input pl-8"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="text-xs text-slate-500 pb-2">
          {filtered.length.toLocaleString()} matched
          {filtered.length !== rows.length && ` of ${rows.length.toLocaleString()}`}
          {' · '}<span className="font-medium text-slate-700">{formatCurrency(totalAmount)}</span>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH col="synced_at"       label="Sync Date"     sort={sort} onSort={handleSort} />
                <SortableTH col="fee_date"        label="Order Date"    sort={sort} onSort={handleSort} />
                <SortableTH col="client_name"     label="Client"        sort={sort} onSort={handleSort} />
                <SortableTH col="order_number"    label="Order #"       sort={sort} onSort={handleSort} />
                <SortableTH col="tracking_number" label="Tracking"      sort={sort} onSort={handleSort} />
                <SortableTH col="manual"          label="Source"        sort={sort} onSort={handleSort} />
                <SortableTH col="currency"        label="Curr."         sort={sort} onSort={handleSort} />
                <SortableTH col="duty_amount"     label="Duty"          sort={sort} onSort={handleSort} align="right" />
                <SortableTH col="tax_amount"      label="Tax"           sort={sort} onSort={handleSort} align="right" />
                <SortableTH col="fee_amount"      label="Fees"          sort={sort} onSort={handleSort} align="right" />
                <SortableTH col="shipping_amount" label="Shipping"      sort={sort} onSort={handleSort} align="right" />
                <SortableTH col="total_amount"    label="Total"         sort={sort} onSort={handleSort} align="right" />
                <SortableTH col="notes"           label="Notes"         sort={sort} onSort={handleSort} />
                <SortableTH col="billing_cycle"   label="Billing Cycle" sort={sort} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 14 }).map((_, j) => (
                      <td key={j} className="px-3 py-3"><div className="skeleton h-3 rounded" /></td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                /* `rows = []` would otherwise show the "no matched fees" state
                   below, and the counter above would read 0 matched · $0.00 as
                   though that were a real total. */
                <tr>
                  <td colSpan={14} className="py-12 text-center">
                    <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
                    <p className="text-red-600 text-sm font-medium">Failed to load matched export fees</p>
                    <p className="text-red-500 text-xs mt-1">
                      This is a load failure, <strong>not</strong> an empty result — the count and
                      total above are from the failed fetch, not from the data.
                    </p>
                    <p className="text-slate-400 text-xs mt-1 font-mono">
                      {error?.response?.data?.detail || error?.message || 'Unknown error'}
                    </p>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={14} className="py-12 text-center">
                    <CheckSquare size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-400 text-sm">{search ? 'No results match your search' : 'No matched export fees yet'}</p>
                  </td>
                </tr>
              ) : pageRows.map((r) => (
                <tr key={r.id} className={`hover:bg-slate-50 transition-colors ${!r.billing_cycle ? 'bg-amber-50/30' : ''}`}>
                  <td className="px-3 py-2 text-slate-600">{r.synced_at ? r.synced_at.slice(0, 10) : '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{r.fee_date || '—'}</td>
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{r.client_name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-slate-700">{r.order_number || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{r.tracking_number || '—'}</td>
                  <td className="px-3 py-2">
                    {(() => {
                      const src = classifySource(r)
                      return (
                        <span
                          className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded ${src.cls}`}
                          title={
                            src.key === 'fedex' ? 'Auto-ingested from a FedEx duty PDF.'
                            : src.key === 'manual' ? `Manually entered${r.courier ? ` (${r.courier})` : ''}`
                            : 'Synced from Zonos.'
                          }
                        >
                          {src.label}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.currency || 'AUD'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.duty_amount, r.currency)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.tax_amount, r.currency)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.fee_amount, r.currency)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.shipping_amount, r.currency)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{formatCurrency(r.total_amount, r.currency)}</td>
                  <td className="px-3 py-2 text-slate-500 max-w-[200px] truncate" title={r.notes || ''}>{r.notes || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.billing_cycle ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {r.billing_cycle}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        Not billed yet
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
            <div className="flex items-center gap-3">
              <p className="text-xs text-slate-500">
                Showing {((safePage - 1) * pageSize) + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length.toLocaleString()}
              </p>
              <select
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1) }}
                className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 bg-white"
              >
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n} per page</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Page {safePage} of {totalPages}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                  className="px-2 py-1 text-xs rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600">←</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                  className="px-2 py-1 text-xs rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600">→</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function UnmatchedExports() {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const [activeTab, setActiveTab] = useState('unmatched')
  const [ordersSyncing,  setOrdersSyncing]  = useState(false)
  const [exportsSyncing, setExportsSyncing] = useState(false)

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['exports', 'unmatched'],
    queryFn: () => getUnmatchedExportFees(),
    refetchInterval: 60_000,
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: getAdminClients,
  })

  // Sort clients alphabetically for the dropdown
  const sortedClients = [...clients].sort((a, b) => a.name.localeCompare(b.name))

  const totalValue = rows.reduce((s, r) => s + (r.total_amount ?? 0), 0)

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['exports', 'unmatched'] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
    queryClient.invalidateQueries({ queryKey: ['sync', 'status'] })
  }

  async function handleOrdersSync() {
    setOrdersSyncing(true)
    try {
      const result = await syncOrders(false)
      if (result.success) {
        addToast({
          type: 'success',
          message: `Orders sync complete — ${result.orders_created ?? 0} new, ${result.orders_updated ?? 0} updated. Now run Exports Sync to re-match.`,
          duration: 8000,
        })
      } else {
        addToast({ type: 'error', message: `Orders sync errors: ${result.errors?.slice(0, 2).join(' · ')}`, duration: 8000 })
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
      queryClient.invalidateQueries({ queryKey: ['sync', 'status'] })
    } catch (err) {
      addToast({ type: 'error', message: `Orders sync failed: ${err.response?.data?.detail || err.message}` })
    } finally {
      setOrdersSyncing(false)
    }
  }

  async function handleExportsSync() {
    setExportsSyncing(true)
    const now  = new Date()
    const to   = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    const from = `${now.getFullYear()}-01-01`
    try {
      const result = await syncExportFees(from, to)
      if (result.success) {
        addToast({
          type: 'success',
          message: `Exports sync complete — ${result.fetched} fetched, ${result.matched} matched, ${result.unmatched} unmatched, ${result.skipped} skipped.`,
          duration: 6000,
        })
      } else {
        addToast({ type: 'error', message: `Exports sync errors: ${result.errors?.join(' · ')}`, duration: 8000 })
      }
      invalidateAll()
    } catch (err) {
      addToast({ type: 'error', message: `Exports sync failed: ${err.response?.data?.detail || err.message}` })
    } finally {
      setExportsSyncing(false)
    }
  }

  const busy = ordersSyncing || exportsSyncing

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="space-y-6">

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Unmatched Exports</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Zonos &amp; FedEx duty / tariff charges that could not be attributed to a portal order
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleOrdersSync} disabled={busy} className="btn-secondary text-sm">
              {ordersSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Sync Orders
            </button>
            <button onClick={handleExportsSync} disabled={busy} className="btn-secondary text-sm">
              {exportsSyncing ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />}
              Sync Exports
            </button>
          </div>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('unmatched')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'unmatched'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <List size={14} />
            Unmatched Orders
          </button>
          <button
            onClick={() => setActiveTab('matched')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'matched'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <CheckSquare size={14} />
            Matched Orders
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'invoices'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText size={14} />
            Invoice Summary
          </button>
        </div>

        {activeTab === 'matched'  && <MatchedExportsTab />}
        {activeTab === 'invoices' && <InvoiceSummaryTab />}

        {activeTab === 'unmatched' && <div className="space-y-4">

        {/* How to resolve */}
        <div className="card border-orange-200 bg-orange-50 p-4 text-sm text-orange-700 space-y-1">
          <p className="font-medium">How to resolve unmatched records</p>
          <ol className="list-decimal list-inside space-y-1 text-orange-600 text-xs">
            <li><strong>Automatic:</strong> Click <strong>Sync Orders</strong> then <strong>Sync Exports</strong> — this populates AU Post tracking numbers and re-attempts matching.</li>
            <li><strong>Manual:</strong> Click <strong>Assign</strong> on any row to assign it directly to a client.</li>
          </ol>
        </div>

        {/* Stats bar — gated on !error too: with `rows = []` a failed fetch
            would paint 0 records, $0.00 unattributed and a green "All charges
            matched" tick, which is the opposite of the truth. */}
        {!isLoading && !error && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="card p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Unmatched Records</p>
              <p className={`text-2xl font-bold ${rows.length > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                {rows.length}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Unattributed Value</p>
              <p className={`text-2xl font-bold ${totalValue > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                {formatCurrency(totalValue)}
              </p>
            </div>
            <div className="card p-4 sm:col-span-1 col-span-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Status</p>
              {rows.length === 0 ? (
                <div className="flex items-center gap-2 mt-1">
                  <CheckCircle2 size={20} className="text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-600">All charges matched</span>
                </div>
              ) : (
                <p className="text-sm font-medium text-orange-600 mt-1">Needs attention</p>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : error ? (
            /* Without this branch `rows = []` falls through to the green
               all-clear below, so a 502 reads as "nothing left to attribute". */
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <AlertTriangle size={32} className="text-red-400" />
              <p className="text-sm font-medium text-red-600">Failed to load unmatched export charges</p>
              <p className="text-xs text-red-500 max-w-md text-center">
                This is a load failure, <strong>not</strong> an all-clear — unattributed Zonos &amp; FedEx
                duty records may be sitting here unbilled. Do not treat this as "nothing waiting".
              </p>
              <p className="text-xs text-slate-400 font-mono">
                {error?.response?.data?.detail || error?.message || 'Unknown error'}
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <CheckCircle2 size={32} className="text-emerald-400" />
              <p className="text-sm font-medium text-slate-600">No unmatched export charges</p>
              <p className="text-xs text-slate-400">All Zonos &amp; FedEx duty records have been attributed to clients</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Merchant Order #</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tracking</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Duty</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Tax</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Fees</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Shipping</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Total</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Assign</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-orange-50/40 transition-colors">
                      <td className="px-4 py-3">
                        {(() => {
                          const src = classifySource(row)
                          return (
                            <span
                              className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded ${src.cls}`}
                              title={
                                src.key === 'fedex' ? 'Auto-ingested from a FedEx duty PDF.'
                                : src.key === 'manual' ? `Manually entered${row.courier ? ` (${row.courier})` : ''}`
                                : row.zonos_order_id ? `Zonos ID: ${row.zonos_order_id}` : 'Synced from Zonos.'
                              }
                            >
                              {src.label}
                            </span>
                          )
                        })()}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{row.account_order_number ?? <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.tracking_number ?? <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{row.fee_date ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {row.duty_amount > 0 ? formatCurrency(row.duty_amount, row.currency) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {row.tax_amount > 0 ? formatCurrency(row.tax_amount, row.currency) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {row.fee_amount > 0 ? formatCurrency(row.fee_amount, row.currency) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {row.shipping_amount > 0 ? formatCurrency(row.shipping_amount, row.currency) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-orange-700">
                        {formatCurrency(row.total_amount, row.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <AssignCell
                          feeId={row.id}
                          clients={sortedClients}
                          onAssigned={invalidateAll}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Total ({rows.length} records)
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-700">
                      {formatCurrency(rows.reduce((s, r) => s + (r.duty_amount ?? 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-700">
                      {formatCurrency(rows.reduce((s, r) => s + (r.tax_amount ?? 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-700">
                      {formatCurrency(rows.reduce((s, r) => s + (r.fee_amount ?? 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-700">
                      {formatCurrency(rows.reduce((s, r) => s + (r.shipping_amount ?? 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-orange-700">
                      {formatCurrency(totalValue)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        </div>}

      </div>
    </>
  )
}
